/**
 * UranIA PA — API client documenti workspace.
 *
 * Wrapper minimal su ``GET /api/v1/documenti/{id}`` (metadata) e
 * ``POST /api/v1/documenti`` (crea bozza). Pattern allineato a
 * ``edit-sessions-api.ts`` e ``base-giuridica-api.ts``:
 *   - bearer JWT da ``useAuthStore.accessToken`` (in-memory FR-F1-10);
 *   - ``credentials: 'include'`` per SSO cookie Keycloak;
 *   - Problem+JSON RFC 9457 parser su 4xx/5xx.
 */

import { useAuthStore } from '../auth/auth-store.ts';
import { loadSpaEnv } from '../env.ts';

export type DocumentoTipo =
  | 'base_giuridica'
  | 'avviso'
  | 'decreto_graduatoria'
  | 'decreto_finanziamento'
  | 'checklist_ricevibilita'
  | 'checklist_ammissibilita';

export interface DocumentoDto {
  readonly id: string;
  readonly tenant_id: string;
  readonly tipo: DocumentoTipo | string;
  readonly stato: string;
  readonly titolo: string;
  readonly azione_fesr: string | null;
  readonly s3_key: string | null;
  readonly data_classification: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly version: number;
}

interface CreateDocumentoResponse {
  readonly documento: DocumentoDto;
}

export class DocumentoApiError extends Error {
  public readonly status: number;
  public readonly detail: string | null;

  constructor(status: number, title: string, detail: string | null) {
    super(title);
    this.name = 'DocumentoApiError';
    this.status = status;
    this.detail = detail;
  }
}

function authHeader(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  if (token === null || token.length === 0) {
    return {};
  }
  return { Authorization: `Bearer ${token}` };
}

function buildUrl(path: string): string {
  const env = loadSpaEnv();
  const base = env.apiBaseUrl.replace(/\/+$/u, '');
  return `${base}${path}`;
}

async function parseProblem(response: Response): Promise<DocumentoApiError> {
  let title = response.statusText || 'http_error';
  let detail: string | null = null;
  try {
    const body = (await response.json()) as { title?: unknown; detail?: unknown };
    if (typeof body.title === 'string' && body.title.length > 0) {
      title = body.title;
    }
    if (typeof body.detail === 'string') {
      detail = body.detail;
    }
  } catch {
    // server senza Problem+JSON, fallback su statusText.
  }
  return new DocumentoApiError(response.status, title, detail);
}

export async function getDocumento(
  documentoId: string,
  signal?: AbortSignal,
): Promise<DocumentoDto> {
  const init: RequestInit = {
    method: 'GET',
    headers: { Accept: 'application/json', ...authHeader() },
    credentials: 'include',
  };
  if (signal !== undefined) {
    init.signal = signal;
  }
  const response = await fetch(
    buildUrl(`/api/v1/documenti/${encodeURIComponent(documentoId)}`),
    init,
  );
  if (!response.ok) {
    throw await parseProblem(response);
  }
  return (await response.json()) as DocumentoDto;
}

const DEFAULT_TITLES: Record<DocumentoTipo, string> = {
  base_giuridica: 'Nuova Base Giuridica',
  avviso: 'Nuovo Avviso',
  decreto_graduatoria: 'Nuovo Decreto graduatoria',
  decreto_finanziamento: 'Nuovo Decreto finanziamento',
  checklist_ricevibilita: 'Nuova Checklist ricevibilità',
  checklist_ammissibilita: 'Nuova Checklist ammissibilità',
};

export async function createDocumento(
  tipo: DocumentoTipo,
  signal?: AbortSignal,
): Promise<DocumentoDto> {
  const now = new Date().toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const titolo = `${DEFAULT_TITLES[tipo]} — ${now}`;
  const init: RequestInit = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...authHeader(),
    },
    credentials: 'include',
    body: JSON.stringify({ tipo, titolo }),
  };
  if (signal !== undefined) {
    init.signal = signal;
  }
  const response = await fetch(buildUrl('/api/v1/documenti'), init);
  if (!response.ok) {
    throw await parseProblem(response);
  }
  const body = (await response.json()) as CreateDocumentoResponse | DocumentoDto;
  if ('documento' in body) {
    return body.documento;
  }
  return body;
}

export interface ListDocumentiDto {
  readonly items: readonly DocumentoDto[];
  readonly total: number;
}

export async function listDocumenti(): Promise<ListDocumentiDto> {
  const response = await fetch(buildUrl('/api/v1/documenti'), {
    method: 'GET',
    headers: { Accept: 'application/json', ...authHeader() },
    credentials: 'include',
  });
  if (!response.ok) {
    throw await parseProblem(response);
  }
  return (await response.json()) as ListDocumentiDto;
}
